import { AxisKind } from '@atlas/types'
import { CardShell, Field, SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { MergeValueDialog } from '@/components/merge/MergeValueDialog'
import {
	useAxis,
	useCreateValue,
	useMergeValue,
	useRetireAxis,
	useRetireValue,
	useUpdateAxis,
	useValues,
} from '@/hooks/useClassification'
import type { AxisValueDTO } from '@/services/classification.service'
import { AxisValueList } from './AxisValueList'
import { LabelledCombobox, TextField } from './FormField'

/**
 * One axis: what it IS, and every value on it.
 *
 * The same page for all thirty axes — hierarchical or flat, dimension or attribute — so
 * learning one teaches the rest. What differs is data, not layout: a hierarchical axis
 * indents its values and offers a parent when adding one, and nothing else changes.
 *
 * Editing a VALUE happens on its own page rather than inline. A value carries per-market
 * names, availability and external codes, which is more than a row can hold.
 */

const VALUE_TYPES = ['list', 'text', 'url', 'date', 'number']
const STATUSES = [
	{ value: 'active', label: 'active' },
	{ value: 'retired', label: 'retired' },
]

/** The axis's own fields. `code` is immutable — everything references it. */
function AxisFields({ axisId }: { axisId: string }) {
	const { axis } = useAxis(axisId)
	const update = useUpdateAxis()
	const [form, setForm] = useState({ name: '', valueType: 'list', isHierarchical: false, status: 'active' })

	// Seeded from the axis once it arrives, not on every render: a refetch mid-edit would
	// otherwise discard what was typed.
	useEffect(() => {
		if (!axis) return
		setForm({
			name: axis.name,
			valueType: axis.valueType,
			isHierarchical: axis.isHierarchical,
			status: axis.status,
		})
	}, [axis])

	if (!axis) return null
	const dirty =
		form.name !== axis.name ||
		form.valueType !== axis.valueType ||
		form.isHierarchical !== axis.isHierarchical ||
		form.status !== axis.status

	return (
		<SectionCard title="Axis" description="What this axis is. Its code is permanent — everything references it.">
			<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<Field label="Code" value={axis.code} readOnly description="Immutable — everything references it." />
				<Field label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
				<LabelledCombobox
					label="Value type"
					value={form.valueType}
					onChange={(valueType) => setForm({ ...form, valueType: valueType ?? form.valueType })}
					options={VALUE_TYPES.map((v) => ({ value: v, label: v }))}
				/>
				<LabelledCombobox
					label="Status"
					value={form.status}
					onChange={(status) => setForm({ ...form, status: status ?? form.status })}
					options={STATUSES}
				/>
			</div>
			{/* Kind is shown, never edited: it decides what an UNTAGGED axis means — a
			    dimension applies to every value, an attribute matches nothing (ADR-0007
			    §4b) — so changing it re-reads every object already classified here. */}
			<p className="mt-3 text-ds-text-muted text-xs">
				<span className="text-ds-text">{axis.kind === AxisKind.DIMENSION ? 'Dimension' : 'Attribute'}</span>{' '}
				{axis.kind === AxisKind.DIMENSION
					? '— matched through the hierarchy. A value left untagged applies to every value on this axis.'
					: '— matched by equality. A value left untagged matches nothing on this axis.'}{' '}
				Changing this would redefine every existing tag, so it is not editable here.
			</p>
			<div className="mt-4 flex justify-end">
				<Button
					type="button"
					className="rounded-ds-full px-5"
					isDisabled={!dirty || update.isPending || !form.name.trim()}
					onPress={() => update.mutate({ axisId, body: form })}
				>
					{update.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</SectionCard>
	)
}

/** Add a value, with a parent where the axis has a hierarchy to put it in. */
function AddValue({ axisId, hierarchical, values }: { axisId: string; hierarchical: boolean; values: AxisValueDTO[] }) {
	const createValue = useCreateValue(axisId)
	const [form, setForm] = useState({ code: '', label: '', parentId: '' })

	const submit = () => {
		if (!form.code.trim() || !form.label.trim()) return
		createValue.mutate(
			{ code: form.code.trim(), label: form.label.trim(), parentId: form.parentId || undefined },
			{ onSuccess: () => setForm({ code: '', label: '', parentId: '' }) },
		)
	}

	return (
		<SectionCard title="Add value" description="Code is permanent; the label is free to change later.">
			<div className="flex flex-wrap items-end gap-3">
				<TextField label="Code" value={form.code} onChange={(code) => setForm({ ...form, code })} placeholder="beef" />
				<TextField
					label="Label"
					value={form.label}
					onChange={(label) => setForm({ ...form, label })}
					placeholder="Beef"
				/>
				{hierarchical && (
					<LabelledCombobox
						label="Parent"
						value={form.parentId}
						onChange={(parentId) => setForm({ ...form, parentId: parentId ?? '' })}
						// An empty selection is a meaningful choice — a value with no parent
						// is a root — so it is named rather than left blank.
						placeholder="(root)"
						options={values.map((v) => ({ value: v.valueId, label: v.label }))}
					/>
				)}
				<Button
					className="rounded-ds-full"
					isDisabled={createValue.isPending || !form.code.trim() || !form.label.trim()}
					onPress={submit}
				>
					{createValue.isPending ? 'Adding…' : 'Add'}
				</Button>
			</div>
		</SectionCard>
	)
}

/**
 * The three confirmations this page can raise, kept together so the page above them reads
 * as what it shows rather than what it asks.
 */
function AxisDialogs({
	axisId,
	axisName,
	values,
	retiring,
	merging,
	retiringAxis,
	onCloseRetire,
	onCloseMerge,
	onCloseRetireAxis,
}: {
	axisId: string
	axisName: string
	values: AxisValueDTO[]
	retiring: AxisValueDTO | null
	merging: AxisValueDTO | null
	retiringAxis: boolean
	onCloseRetire: () => void
	onCloseMerge: () => void
	onCloseRetireAxis: () => void
}) {
	const navigate = useNavigate()
	const retireValue = useRetireValue(axisId)
	const mergeValue = useMergeValue(axisId)
	const retireAxis = useRetireAxis()

	return (
		<>
			<MergeValueDialog
				open={merging !== null}
				subject={merging}
				candidates={values}
				submitting={mergeValue.isPending}
				onConfirm={(into) => {
					if (!merging) return
					mergeValue.mutate({ valueId: merging.valueId, into }, { onSettled: () => onCloseMerge() })
				}}
				onCancel={onCloseMerge}
			/>
			<ConfirmDialog
				open={retiring !== null}
				title={`Retire “${retiring?.label ?? ''}”?`}
				description="It stops being offered. Nothing tagged with it is lost, and it can be restored by editing its status."
				confirmLabel="Retire value"
				confirmVariant="danger"
				busyLabel="Retiring…"
				submitting={retireValue.isPending}
				onConfirm={() => {
					if (!retiring) return
					retireValue.mutate(retiring.valueId, { onSettled: () => onCloseRetire() })
				}}
				onCancel={onCloseRetire}
			/>
			<ConfirmDialog
				open={retiringAxis}
				title={`Retire the “${axisName}” axis?`}
				description="It stops being offered everywhere. Every tag filed against it is kept and still resolves."
				confirmLabel="Retire axis"
				confirmVariant="danger"
				busyLabel="Retiring…"
				submitting={retireAxis.isPending}
				onConfirm={() =>
					retireAxis.mutate(axisId, {
						onSuccess: () => void navigate('/admin/classification'),
						onSettled: () => onCloseRetireAxis(),
					})
				}
				onCancel={onCloseRetireAxis}
			/>
		</>
	)
}

export function AxisDetailPage() {
	const { axisId = '' } = useParams()
	const { axis } = useAxis(axisId)
	const { data: values = [], isLoading } = useValues(axisId)
	const [retiring, setRetiring] = useState<AxisValueDTO | null>(null)
	const [merging, setMerging] = useState<AxisValueDTO | null>(null)
	const [retiringAxis, setRetiringAxis] = useState(false)

	return (
		<div className="space-y-6">
			<div>
				<Link to="/admin/classification" className="text-ds-info text-sm hover:underline">
					← Axis registry
				</Link>
				<h1 className="mt-1 font-semibold text-2xl">
					{axis ? axis.name : 'Axis'} <span className="text-base text-ds-text-subtle">{axis?.code}</span>
				</h1>
				<p className="text-ds-text-muted">
					{isLoading ? 'Loading…' : `${values.length} value${values.length === 1 ? '' : 's'}`}
					{axis?.isHierarchical ? ' · hierarchical' : ''}
					{axis?.status === 'retired' ? ' · retired' : ''}
				</p>
			</div>

			<AxisFields axisId={axisId} />
			<AddValue axisId={axisId} hierarchical={!!axis?.isHierarchical} values={values} />

			<CardShell>
				<AxisValueList
					axisId={axisId}
					values={values}
					hierarchical={!!axis?.isHierarchical}
					onRetire={(valueId: string) => setRetiring(values.find((v) => v.valueId === valueId) ?? null)}
					onMerge={(valueId: string) => setMerging(values.find((v) => v.valueId === valueId) ?? null)}
				/>
			</CardShell>

			{/* Retiring the axis is separated from the fields above: it is the one action
			    here that changes what the rest of ATLAS can classify. */}
			{axis?.status !== 'retired' && (
				<SectionCard title="Retire this axis" description="It stops being offered. Nothing filed against it is lost.">
					<div className="flex flex-wrap items-center gap-3">
						<p className="flex-1 text-ds-text-muted text-sm">
							Every object tagged on this axis keeps its tags, and they still resolve — the axis simply stops appearing
							anywhere a value can be picked.
						</p>
						<Button type="button" variant="danger" className="rounded-ds-full" onPress={() => setRetiringAxis(true)}>
							Retire axis
						</Button>
					</div>
				</SectionCard>
			)}

			<AxisDialogs
				axisId={axisId}
				axisName={axis?.name ?? ''}
				values={values}
				retiring={retiring}
				merging={merging}
				retiringAxis={retiringAxis}
				onCloseRetire={() => setRetiring(null)}
				onCloseMerge={() => setMerging(null)}
				onCloseRetireAxis={() => setRetiringAxis(false)}
			/>
		</div>
	)
}
